import { X } from "lucide-react";
import { WorthItAsk } from "@/components/app/DecisionPicture";
import CompareFacts from "@/components/app/CompareFacts";
import { COMPARE_SECTIONS, THEME_LABELS, compareOptions } from "@/lib/decisionCompare";

function Line({ item, testId, onChanged }) {
  return (
    <li className="min-w-0 break-words [overflow-wrap:anywhere] text-[12px] text-stone-700" data-testid={testId}>
      {item.label}
      {item.detail && <span className="block text-[11px] text-stone-500">{item.detail}</span>}
      {item.worth_it && <WorthItAsk ask={item.worth_it} testId={testId + "-worth-it"} onChanged={onChanged} />}
    </li>
  );
}

function SharedBlock({ title, entries, names, testId, onChanged }) {
  if (!entries.length) return null;
  return (
    <div className="rounded-md border border-violet-200 bg-violet-50/50 px-3 py-2" data-testid={testId}>
      <div className="text-[11px] font-semibold text-[#4C1D95]">{title}</div>
      <ul className="mt-1 space-y-1.5">
        {entries.map((e, i) =>
          e.same ? (
            <Line key={i} item={e.item} testId={testId + "-" + i} onChanged={onChanged} />
          ) : (
            <li key={i} className="min-w-0 text-[12px] text-stone-700" data-testid={testId + "-" + i}>
              <span className="font-medium">{THEME_LABELS[e.semantic] || "Shared theme"}</span>
              <ul className="mt-0.5 space-y-1 border-l border-violet-200 pl-2">
                {[e.left, e.right].map((x, s) => (
                  <li key={s} className="min-w-0 break-words [overflow-wrap:anywhere]" data-testid={testId + "-" + i + "-" + (s ? "b" : "a")}>
                    <span className="text-[10px] font-underground-sc tracking-wider text-stone-500">{names[s]}</span>
                    <Line item={x} testId={testId + "-" + i + "-" + (s ? "b" : "a") + "-line"} onChanged={onChanged} />
                  </li>
                ))}
              </ul>
            </li>
          ),
        )}
      </ul>
    </div>
  );
}

function SideList({ items, name, testId, onChanged }) {
  return (
    <div className="min-w-0" data-testid={testId}>
      <div className="text-[10px] font-underground-sc tracking-wider text-stone-500 md:hidden">{name}</div>
      {items.length ? (
        <ul className="mt-0.5 space-y-1">
          {items.map((x, i) => <Line key={i} item={x} testId={testId + "-" + i} onChanged={onChanged} />)}
        </ul>
      ) : (
        <div className="text-[11px] italic text-stone-400">Nothing here for this option.</div>
      )}
    </div>
  );
}

function Header({ choice, side, onRemove }) {
  return (
    <div className="min-w-0 rounded-md border border-stone-200 bg-white px-3 py-2" data-testid={"compare-header-" + side}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-underground-sc tracking-wider text-stone-500">Option {side.toUpperCase()}</div>
          <div className="font-editorial text-sm leading-tight break-words">{choice.title}</div>
          <div className="text-[11px] text-stone-500 break-words">{choice.place}</div>
        </div>
        <button type="button" onClick={() => onRemove(choice.key)} aria-label={"Remove " + choice.title + " from compare"} data-testid={"compare-remove-" + side}
          className="rounded p-0.5 text-stone-400 transition-colors hover:text-stone-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4C1D95]">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default function CompareDecisions({ choices, onRemove, onChanged, nonce }) {
  if (choices.length < 2) {
    return (
      <div className="mt-4 rounded-lg border border-dashed border-stone-300 bg-stone-50 px-4 py-3 text-sm text-stone-600" data-testid="compare-prompt">
        {choices.length === 0
          ? "Pick two choices below to see them side by side."
          : "Pick one more choice to compare with " + choices[0].title + "."}
      </div>
    );
  }
  const [a, b] = choices;
  const cmp = compareOptions(a.picture, b.picture);
  const names = [a.title, b.title];
  return (
    <section className="mt-4 min-w-0 rounded-lg border border-stone-200 bg-stone-50/60 p-3 sm:p-4" data-testid="compare-panel">
      <div className="text-[10px] font-underground-sc tracking-[0.14em] text-stone-500">Side by side · descriptive only, no ranking</div>
      <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
        <Header choice={a} side="a" onRemove={onRemove} />
        <Header choice={b} side="b" onRemove={onRemove} />
      </div>
      <CompareFacts keys={[a.key, b.key]} names={names} nonce={nonce} />
      <div className="mt-4 text-[11px] font-semibold text-stone-700">What you've told WUA</div>
      {!COMPARE_SECTIONS.some(({ key }) => cmp[key].shared.length || cmp[key].left.length || cmp[key].right.length) && (
        <p className="mt-1 text-[12px] italic text-stone-500" data-testid="compare-told-empty">
          Nothing you've confirmed on WUA applies to either choice yet. That doesn't mean either one fits or doesn't.
        </p>
      )}
      <div className="mt-2 space-y-3">
        {COMPARE_SECTIONS.map(({ key, shared, side }) => {
          const c = cmp[key];
          if (!c.shared.length && !c.left.length && !c.right.length) return null;
          return (
            <div key={key} className="min-w-0" data-testid={"compare-section-" + key}>
              <SharedBlock title={shared} entries={c.shared} names={names} testId={"compare-shared-" + key} onChanged={onChanged} />
              {(c.left.length > 0 || c.right.length > 0) && (
                <div className="mt-2">
                  <div className="text-[11px] font-semibold text-stone-700">{side}</div>
                  <div className="mt-1 grid grid-cols-1 gap-3 md:grid-cols-2">
                    <SideList items={c.left} name={a.title} testId={"compare-a-" + key} onChanged={onChanged} />
                    <SideList items={c.right} name={b.title} testId={"compare-b-" + key} onChanged={onChanged} />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
